import React, { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { AppPillSwitch } from '../ui';
import KkpCronAlertCard from './KkpCronAlertCard';
import {
  cronMatchesSearch,
  getCronTypeUi,
  isInstantCronType,
  isSystemCronType,
} from '../../utils/kkpCronUi';

const CronTypeGroup = ({
  type,
  crons,
  commerciaux,
  showOwner,
  saving,
  onCreateType,
  onEdit,
  onDuplicate,
  onToggleActive,
  onSend,
  onDelete,
}) => {
  const ui = getCronTypeUi(type.id);
  const isSystem = Boolean(type.system) || isSystemCronType(type.id);
  const isInstant = Boolean(type.instant) || isInstantCronType(type.id);
  const active = crons.filter((cron) => cron.active);
  const paused = crons.filter((cron) => !cron.active);
  const [pausedOpen, setPausedOpen] = useState(active.length === 0 && paused.length > 0);
  const [groupOpen, setGroupOpen] = useState(isInstant);

  return (
    <section className={`kkp-crons-group ${isSystem ? 'is-system' : ''}`}>
      <header className="kkp-crons-group__head">
        <button
          type="button"
          className="kkp-crons-group__title"
          onClick={() => setGroupOpen((open) => !open)}
          aria-expanded={groupOpen}
        >
          <span className="kkp-crons-group__icon" style={{ backgroundColor: `${ui.color}18`, color: ui.color }}>
            <Icon icon={ui.icon} width={18} />
          </span>
          <span className="kkp-crons-group__name">{isSystem ? `${type.label} (sans mail)` : type.label}</span>
          <span className="kkp-crons-group__count">{crons.length}</span>
          <Icon icon={groupOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={20} />
        </button>
        {isSystem || isInstant ? null : (
          <button
            type="button"
            className="btn btn-outline-primary kkp-crons-icon-btn"
            onClick={() => onCreateType(type.id)}
            title="Ajouter une alerte"
            aria-label="Ajouter une alerte"
          >
            <Icon icon="mdi:plus" width={20} />
          </button>
        )}
      </header>

      {groupOpen && (
        <>
          {active.length === 0 && paused.length === 0 ? (
            <p className="kkp-crons-group__empty">Aucune alerte pour l’instant. Cliquez sur Ajouter pour en créer une.</p>
          ) : null}

          <div className="kkp-crons-group__cards">
            {active.map((cron) => (
              <KkpCronAlertCard
                key={cron.id}
                cron={cron}
                commerciaux={commerciaux}
                showOwner={showOwner}
                saving={saving}
                onEdit={onEdit}
                onDuplicate={onDuplicate}
                onToggleActive={onToggleActive}
                onSend={onSend}
                onDelete={onDelete}
              />
            ))}
          </div>

          {paused.length > 0 && (
            <div className="kkp-crons-paused">
              <button
                type="button"
                className="kkp-crons-paused__toggle"
                onClick={() => setPausedOpen((open) => !open)}
                aria-expanded={pausedOpen}
              >
                <Icon icon={pausedOpen ? 'mdi:chevron-down' : 'mdi:chevron-right'} width={18} />
                {paused.length} alerte{paused.length > 1 ? 's' : ''} en pause (elles ne s’envoient plus)
              </button>
              {pausedOpen && (
                <div className="kkp-crons-group__cards">
                  {paused.map((cron) => (
                    <KkpCronAlertCard
                      key={cron.id}
                      cron={cron}
                      commerciaux={commerciaux}
                      showOwner={showOwner}
                      saving={saving}
                      onEdit={onEdit}
                      onDuplicate={onDuplicate}
                      onToggleActive={onToggleActive}
                      onSend={onSend}
                      onDelete={onDelete}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
};

const KkpCronsList = ({
  crons,
  cronTypes,
  commerciaux,
  isSuperAdmin,
  currentUserId,
  ownerScope,
  onOwnerScopeChange,
  search,
  onSearchChange,
  saving,
  onCreateType,
  onEdit,
  onDuplicate,
  onToggleActive,
  onSend,
  onDelete,
}) => {
  const filtered = useMemo(() => {
    let list = crons;
    if (isSuperAdmin && ownerScope === 'mine') {
      list = list.filter((cron) => Number(cron.created_by_id) === Number(currentUserId));
    }
    return list.filter((cron) => cronMatchesSearch(cron, search, commerciaux));
  }, [commerciaux, crons, currentUserId, isSuperAdmin, ownerScope, search]);

  const groups = useMemo(() => {
    const knownIds = new Set((cronTypes || []).map((type) => type.id));
    const byType = (cronTypes || []).map((type) => ({
      type,
      crons: filtered.filter((cron) => cron.cron_type === type.id),
    }));
    const unknown = filtered.filter((cron) => !knownIds.has(cron.cron_type));
    if (unknown.length > 0) {
      byType.push({
        type: { id: 'autres', label: 'Autres mails', description: '' },
        crons: unknown,
      });
    }
    return byType;
  }, [cronTypes, filtered]);

  const groupsWithCrons = groups.filter((group) => group.crons.length > 0);
  const emptyTypes = (cronTypes || []).filter((type) => (
    !type.system && !type.instant && !isSystemCronType(type.id) && !isInstantCronType(type.id)
    && !filtered.some((cron) => cron.cron_type === type.id)
  ));
  const hasSearch = String(search || '').trim() !== '';

  return (
    <div className="kkp-crons-list">
      <div className="kkp-crons-toolbar">
        <div className="kkp-crons-toolbar__search">
          <Icon icon="mdi:magnify" width={18} />
          <input
            type="search"
            className="form-control"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Chercher par nom ou par personne…"
            aria-label="Chercher une alerte par nom ou par personne"
          />
        </div>
        {isSuperAdmin ? (
          <AppPillSwitch
            options={[
              { value: 'all', label: 'Toutes', count: crons.length },
              {
                value: 'mine',
                label: 'Les miennes',
                count: crons.filter((cron) => Number(cron.created_by_id) === Number(currentUserId)).length,
              },
            ]}
            value={ownerScope}
            onChange={onOwnerScopeChange}
            ariaLabel="Filtrer par créateur"
          />
        ) : null}
      </div>

      {filtered.length === 0 ? (
        <div className="kkp-crons-empty">
          <Icon icon="mdi:email-off-outline" width={40} />
          <p className="kkp-crons-empty__title">
            {hasSearch || (isSuperAdmin && ownerScope === 'mine')
              ? 'Aucune alerte ne correspond.'
              : 'Vous n’avez pas encore d’alerte.'}
          </p>
          <p className="kkp-crons-empty__text">
            {hasSearch
              ? 'Essayez un autre mot, ou effacez la recherche.'
              : 'Choisissez le type de mail à envoyer. On vous guide ensuite, question par question.'}
          </p>
          {!hasSearch && (
            <div className="kkp-crons-type-grid mt-3 w-100">
              {cronTypes.filter((type) => !type.system && !type.instant && !isSystemCronType(type.id) && !isInstantCronType(type.id)).map((type) => {
                const ui = getCronTypeUi(type.id);
                return (
                  <button
                    key={type.id}
                    type="button"
                    className="kkp-crons-type-card"
                    onClick={() => onCreateType(type.id)}
                  >
                    <span className="kkp-crons-type-card__icon" style={{ backgroundColor: `${ui.color}18`, color: ui.color }}>
                      <Icon icon={ui.icon} width={26} />
                    </span>
                    <span className="kkp-crons-type-card__text">
                      <span className="kkp-crons-type-card__label">{type.label}</span>
                      <span className="kkp-crons-type-card__blurb">{ui.blurb || type.description || 'Créer cette alerte'}</span>
                    </span>
                    <span className="kkp-crons-type-card__go">
                      Choisir
                      <Icon icon="mdi:chevron-right" width={18} />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        groupsWithCrons.map((group) => (
          <CronTypeGroup
            key={group.type.id}
            type={group.type}
            crons={group.crons}
            commerciaux={commerciaux}
            showOwner={isSuperAdmin && ownerScope === 'all'}
            saving={saving}
            onCreateType={onCreateType}
            onEdit={onEdit}
            onDuplicate={onDuplicate}
            onToggleActive={onToggleActive}
            onSend={onSend}
            onDelete={onDelete}
          />
        ))
      )}

      {!hasSearch && emptyTypes.length > 0 && filtered.length > 0 && (
        <section className="kkp-crons-more">
          <p className="kkp-crons-more__title">Vous pouvez aussi créer</p>
          <div className="kkp-crons-more__chips">
            {emptyTypes.map((type) => {
              const ui = getCronTypeUi(type.id);
              return (
                <button
                  key={type.id}
                  type="button"
                  className="kkp-crons-more__chip"
                  onClick={() => onCreateType(type.id)}
                >
                  <Icon icon={ui.icon} width={16} />
                  {type.label}
                </button>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default KkpCronsList;
